const BASE_URL = import.meta.env.VITE_BACKEND_URL;
const API_PREFIX = "/api";

export interface ApiSuccessResponse<T> {
  success: boolean;
  data: T;
  meta: { timestamp: string; correlationId: string };
}

export interface ApiErrorBody {
  message: string;
  code?: string | number;
  statusCode: number;
}

export class ApiError extends Error {
  readonly statusCode: number;
  readonly code?: string | number;

  constructor({ message, code, statusCode }: ApiErrorBody) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.code = code;
  }
}

interface ErrorEnvelope {
  success?: boolean;
  message?: string;
  code?: string | number;
  error?: {
    message?: string;
    userMessage?: string;
    code?: string | number;
    errorCode?: string;
  };
}

function catalogCode(body: ErrorEnvelope | undefined): string | number | undefined {
  const errorCode = body?.error?.errorCode;
  if (typeof errorCode === "string" && errorCode) return errorCode;
  const nested = body?.error?.code;
  if (typeof nested === "string") return nested;
  if (typeof body?.code === "string") return body.code;
  return undefined;
}

function catalogMessage(body: ErrorEnvelope | undefined, fallback: string): string {
  const userMessage = body?.error?.userMessage;
  if (typeof userMessage === "string" && userMessage) return userMessage;
  const nested = body?.error?.message;
  if (typeof nested === "string" && nested) return nested;
  if (typeof body?.message === "string" && body.message) return body.message;
  return fallback;
}

export function apiErrorFromBody(
  statusCode: number,
  body: unknown,
  fallback: string,
): ApiError {
  const envelope = (body ?? {}) as ErrorEnvelope;
  return new ApiError({
    message: catalogMessage(envelope, fallback),
    code: catalogCode(envelope),
    statusCode,
  });
}

function isFailureEnvelope(body: unknown): boolean {
  return (
    typeof body === "object" &&
    body !== null &&
    "success" in body &&
    (body as { success: unknown }).success === false
  );
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const normalizedBaseUrl = BASE_URL.replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const prefixedPath =
    normalizedPath === API_PREFIX || normalizedPath.startsWith(`${API_PREFIX}/`)
      ? normalizedPath
      : `${API_PREFIX}${normalizedPath}`;

  const response = await fetch(`${normalizedBaseUrl}${prefixedPath}`, {
    ...init,
    credentials: "include",
    cache: "no-store",
    headers: {
      ...(init.body !== undefined && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) { // No Content
    if (!response.ok) {
      throw apiErrorFromBody(response.status, undefined, response.statusText);
    }
    return undefined as T;
  }

  let body: unknown;
  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : undefined;
  } catch {
    throw new ApiError({
      message: response.statusText || "Invalid JSON",
      statusCode: response.status,
    });
  }

  if (!response.ok || isFailureEnvelope(body)) {
    throw apiErrorFromBody(response.status, body, response.statusText);
  }

  return body as T;
}

async function requestData<T>(path: string, init: RequestInit = {}): Promise<T> {
  const parsed = await request<ApiSuccessResponse<T>>(path, init);
  return parsed.data;
}

export const apiClient = {
  /** GET request. Returns the raw full JSON response body. */
  get: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: "GET" }),

  /** GET request. Returns the extracted `data` field from the success envelope. */
  getData: <T>(path: string, init?: RequestInit) =>
    requestData<T>(path, { ...init, method: "GET" }),

  /** POST request. Returns the raw full JSON response body. */
  post: <T>(path: string, body: unknown, init?: RequestInit) =>
    request<T>(path, {
      ...init,
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** POST request. Returns the extracted `data` field from the success envelope. */
  postData: <T>(path: string, body: unknown, init?: RequestInit) =>
    requestData<T>(path, {
      ...init,
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** PATCH request. Returns the extracted `data` field from the success envelope. */
  patchData: <T>(path: string, body: unknown, init?: RequestInit) =>
    requestData<T>(path, {
      ...init,
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** DELETE request. Returns the extracted `data` field from the success envelope. */
  deleteData: <T>(path: string, init?: RequestInit) =>
    requestData<T>(path, { ...init, method: "DELETE" }),

  /** PATCH multipart request. Returns the extracted `data` field from the success envelope. */
  patchMultipartData: <T>(path: string, body: FormData, init?: RequestInit) =>
    requestData<T>(path, { ...init, method: "PATCH", body }),
};
